"use client";
import { useRouter } from "next/navigation";
import { TrainingCard } from "../Common/TrainingCard";
import { FilterDrawer } from "@/components/Common/FilterDrawer";
import FilterIcon from "../SVG/FilterIcon";
import SearchIcon from "../SVG/SearchIcon";
import SearchDrawer from "@/components/Common/SearchDrawer";
import CatalogFilterSidebar from "@/components/Common/CatalogFilterSidebar";
import { useGetFormations } from "@/actions/formation";
import { toBookmark } from "@/lib/bookmarks";
import { ENTREPRENEURSHIP_CATEGORY_SLUG } from "@/lib/constants";
import {
  DEFAULT_PRICE_RANGE,
  PRICE_MAX,
  PRICE_MIN,
  useCatalogFilters,
} from "@/hooks/useCatalogFilters";

const CATEGORY_LABEL = "Entreprenariat";

/**
 * Catalog of the /entrepreneurship page — same filtering experience as the
 * main catalog, but locked to the "Entreprenariat" category (which is in
 * turn excluded from the main catalog).
 */
export default function Catalog() {
  const router = useRouter();
  const { formations, formationsLoading } = useGetFormations(
    ENTREPRENEURSHIP_CATEGORY_SLUG,
  );

  const filters = useCatalogFilters(formations);
  const {
    visibleCards,
    query,
    setQuery,
    selectedMode,
    setSelectedMode,
    selectedFinancing,
    priceRange,
    setPriceRange,
    mobileFilterOpen,
    setMobileFilterOpen,
    mobileSearchOpen,
    setMobileSearchOpen,
    handleResetFilters,
    handleToggleFinancing,
    handleRemoveFinancing,
    handleClearPrice,
  } = filters;

  return (
    <section className="md:px-7 px-4 pb-10 pt-12 md:pb-14.25 md:pt-[76px]">
      <div className="max-w-346.25 mx-auto">
        <h2 className="text-center text-[#1B365E] font-light font-red-hat tracking-[0.2px] text-[30px] md:text-[40px] leading-10 md:leading-13.25 md:mb-10 mb-6">
          Formations idéales pour se lancer et réussir dans l&apos;entrepreneuriat
        </h2>
        <div className="flex md:flex-row flex-col md:gap-0 gap-6">
          {/* Sidebar */}
          <aside className="md:pr-6">
            <div className="min-w-70 lg:min-w-81">
              {/* Mobile: search/filter icons */}
              <div className="md:hidden flex items-center justify-end flex-wrap gap-6">
                <button
                  aria-label="search"
                  onClick={() => setMobileSearchOpen(true)}
                  className="shrink-0 w-6 h-6 rounded-full"
                >
                  <SearchIcon className="w-6 h-6" circleColor="#1B365E" strokeColor="#ffffff" />
                </button>
                <button
                  aria-label="filter"
                  onClick={() => setMobileFilterOpen(true)}
                  className="shrink-0 w-6 h-6 rounded-full"
                >
                  <FilterIcon className="w-6 h-6" circleColor="#1B365E" strokeColor="#ffffff" />
                </button>
              </div>

              {/* Desktop filters */}
              <CatalogFilterSidebar controller={filters} />
            </div>
          </aside>

          {/* Main content */}
          <div className="w-full">
            <div className="md:pl-8 md:border-l border-[#40BFDE] md:min-h-160">
              {formationsLoading ? (
                <div className="catalog-grid items-start gap-6 lg:gap-8">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-full h-64 bg-gray-100 animate-pulse rounded-2xl"
                    />
                  ))}
                </div>
              ) : visibleCards.length === 0 ? (
                <p className="text-[#676767] text-sm mt-8">
                  Aucune formation trouvée.
                </p>
              ) : (
                <div className="catalog-grid auto-rows-fr gap-6 lg:gap-8">
                  {visibleCards.map((card, index) => (
                    <div
                      key={card.id}
                      className={`w-full h-full justify-self-center
                        ${index % 2 === 0 ? "lg:justify-self-start" : "lg:justify-self-end"}
                        ${index % 3 === 0 ? "xl:justify-self-start" : index % 3 === 1 ? "xl:justify-self-center" : "xl:justify-self-end"}
                      `}
                    >
                      <TrainingCard
                        title={card.title}
                        description={card.description}
                        category={card.category}
                        location={card.location}
                        duration={card.duration}
                        price={card.price}
                        financing={card.financing ?? ""}
                        isNew={card.isNew}
                        customClass="w-71"
                        formation={toBookmark(card)}
                        onDiscover={() => router.push(`/formation/${card.slug}`)}
                        onSubscribe={() => router.push(`/formation/${card.slug}`)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawers */}
      <SearchDrawer
        open={mobileSearchOpen}
        onClose={() => setMobileSearchOpen(false)}
        categoryLabel={CATEGORY_LABEL}
        showCategoryContext={false}
        initialQuery={query}
        onSubmit={(q) => setQuery(q)}
        onReset={() => setQuery("")}
      />
      <FilterDrawer
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        controller={filters}
        categoryLabel={CATEGORY_LABEL}
        showCategoryContext={false}
        priceMin={PRICE_MIN}
        priceMax={PRICE_MAX}
        priceRange={priceRange}
        defaultPriceRange={DEFAULT_PRICE_RANGE}
        onPriceRangeChange={setPriceRange}
        onClearPrice={handleClearPrice}
        selectedMode={selectedMode}
        onModeChange={setSelectedMode}
        selectedFinancing={selectedFinancing}
        onToggleFinancing={handleToggleFinancing}
        onRemoveFinancing={handleRemoveFinancing}
        onReset={handleResetFilters}
        onApply={() => setMobileFilterOpen(false)}
      />
    </section>
  );
}
